import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Recipe } from '@/types/recipe'
import { Edit, Edit2, Trash2, Share2, Star, Users, Globe, Lock } from 'lucide-react'
import { ShoppingListExport } from './shopping-list-export'

interface UnifiedRecipeActionsProps {
  recipe: Recipe
  isInlineEditMode: boolean
  onToggleInlineEdit: () => void
  onDeleteRecipe: () => void
  onTogglePublicStatus: () => Promise<void>
  className?: string
}

export function UnifiedRecipeActions({
  recipe,
  isInlineEditMode,
  onToggleInlineEdit,
  onDeleteRecipe,
  onTogglePublicStatus,
  className = '',
}: UnifiedRecipeActionsProps) {
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false)

  const handleShare = async () => {
    if (navigator.share) {
      await navigator.share({
        title: recipe.title,
        text: recipe.description,
        url: window.location.href,
      })
      return
    }

    await navigator.clipboard.writeText(window.location.href)
  }

  const handleTogglePublicStatus = async () => {
    setIsUpdatingVisibility(true)
    try {
      await onTogglePublicStatus()
    } finally {
      setIsUpdatingVisibility(false)
    }
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg">Recept Acties</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleInlineEdit}
            className={isInlineEditMode ? 'bg-orange-50 text-orange-600 border-orange-200' : ''}
          >
            <Edit2 className="h-4 w-4 mr-2" />
            {isInlineEditMode ? 'Bewerken uit' : 'Snel bewerken'}
          </Button>

          <Button variant="outline" size="sm" asChild>
            <Link to={`/recepten/${recipe.id}/bewerken`}>
              <Edit className="h-4 w-4 mr-2" />
              Volledig bewerken
            </Link>
          </Button>

          <Button variant="outline" size="sm" onClick={() => void handleShare()}>
            <Share2 className="h-4 w-4 mr-2" />
            Delen
          </Button>
        </div>

        <Separator />

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Recept beheer</h4>
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleTogglePublicStatus()}
              disabled={isUpdatingVisibility}
              className="w-full"
            >
              {recipe.is_public ? (
                <Lock className="h-4 w-4 mr-2" />
              ) : (
                <Globe className="h-4 w-4 mr-2" />
              )}
              {isUpdatingVisibility
                ? 'Bijwerken...'
                : recipe.is_public
                  ? 'Alleen binnen de app houden'
                  : 'Publiek zichtbaar maken'}
            </Button>

            <ShoppingListExport recipe={recipe} variant="button" />

            <Button
              variant="outline"
              size="sm"
              onClick={onDeleteRecipe}
              className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Recept verwijderen
            </Button>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Recept info</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span>{recipe.rating ? `${recipe.rating}/5` : 'Niet beoordeeld'}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Users className="h-4 w-4" />
              <span>{recipe.servings ? `${recipe.servings} porties` : 'Onbekend'}</span>
            </div>
            <div className="col-span-2 flex items-center gap-2 text-gray-600">
              {recipe.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
              <span>{recipe.is_public ? 'Publiek zichtbaar in Zoeken en Bring' : 'Alleen zichtbaar voor jou of je huishouden'}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
